import React, { useState, useEffect } from "react";

import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useLazyQuery, useMutation } from "@apollo/client";
import { VERIFY_CODE, GET_CONTACT_INFORMATION } from "../../graphql/queries";
import { PRODUCT_REMOVE, SUBMIT_DATA, CONFIRM_REGISTRATION, VALIDATE_ADDRESS, UPDATE_CONTACT_INFORMATION } from "../../graphql/mutations";
import Logo from "../logo/logo";
import UserDetailsForm from "../userdetails/userdetailsform";
import CollegeDropdown from "../collegedropdown/collegedropdown";
import FormButtons from "../formbutton/formbuttons";
import NotificationModal from "../common/notificationmodal";
import "../../App.scss";
import InfoSection from "../collegedropdown/informationsection/informationsection";
import ReadOnlyUserDetailsForm from "../readonlyuserdetailsform/readonlyuserdetaisform";
import ContactInformation from "../contactinformation/contactinformation";
import { Box } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import pollEventStream from "./polleventstream";
import { College } from "../collegedropdown/types";
import AddressValidationModal from "../common/addressvalidationmodal";
import {
  validateContactInfo as validateContactInfoShared} from '../../utils/contactvalidation';

type ContactInfoModel = {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  stateCode: string;
  zipCode: string;
  country: string;
};

function MainApplicationForm() {
  // **State Management**
  const [userDetails, setUserDetails] = useState({
    firstName: "",
    lastName: "",
    studentCode: "",
  });

  const enableContactInfo = process.env.REACT_APP_ENABLE_CONTACT_INFO === 'true';
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [initiatedTransactionId, setInitiatedTransactionId] = useState<string | null>(null);
  const [transactionInProgress, setTransactionInProgress] = useState<boolean>(false);
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState<boolean>(false);
  const [isSessionTimeOut, setIsSessionTimeOut] = useState<boolean>(false);
  const [showAddrModal, setShowAddrModal] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<'entered' | 'suggested'>('suggested');
  const [addrResult, setAddrResult] = useState<any>(null);

  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);

  // Manages the list of selected colleges and their order
  const [selectedColleges, setSelectedColleges] = useState<College[]>([]);

  // Contact information state
  const [contactInfo, setContactInfo] = useState<ContactInfoModel>({
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    stateCode: "",
    zipCode: "",
    country: "United States",
  });

  // Handles the modal's open state and notification messages
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [isCodeValidated, setIsCodeValidated] = useState<boolean>(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] = useState<boolean>(false);
  const [isConfirmSubmitModalOpen, setIsConfirmSubmitModalOpen] = useState<boolean>(false);
  const [isRetryPopupVisible, setIsRetryPopupVisible] = useState<boolean>(false);

  useEffect(() => {
    //remove authtoken from localstorage on page load
    localStorage.removeItem('authToken');
  }
    , []);

  // **GraphQL Operations**
  const [validateCode, { data: verifyCodeData }] = useLazyQuery(VERIFY_CODE);

  const [submitColleges] = useMutation(SUBMIT_DATA);

  const [productRemove] = useMutation(PRODUCT_REMOVE);

  const [confirmRegistration] = useMutation(CONFIRM_REGISTRATION);

  const [fetchContactInfo, { data: contactInfoResp }] = useLazyQuery(GET_CONTACT_INFORMATION, {
    fetchPolicy: 'no-cache',
  });

  const [validateAddr, { data: addrData, loading: addrLoading }] = useMutation(VALIDATE_ADDRESS);

  const [updateContactInfo] = useMutation(UPDATE_CONTACT_INFORMATION);

  useEffect(() => {
    if (isCodeValidated) {
      fetchContactInfo();
    }
  }, [isCodeValidated, fetchContactInfo]);

  useEffect(() => {
    const info = contactInfoResp?.contactInformation;
    if (info) {
      const { __typename, ...rest } = info;
      setContactInfo(rest as ContactInfoModel);
    }
  }, [contactInfoResp]);

  const contactErrors = React.useMemo(
    () => (enableContactInfo ? validateContactInfoShared(contactInfo) : {} as Record<string, string>),
    [enableContactInfo, contactInfo]
  );
  const hasContactErrors = enableContactInfo && Object.keys(contactErrors).length > 0;

  // **Event Handlers**
  const handleVerifyCode = async () => {
    try {
      const response = await validateCode({
        variables: {
          authenticateStudentInput: {
            claimCode: userDetails.studentCode,
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
          },
        },
      });
      const { authenticateStudentResponse } = response?.data || {};

      if (authenticateStudentResponse?.code === 'Success') {
        const token = authenticateStudentResponse?.token;

        if (token) {
          // Save token for future use, e.g., to local storage or a state management solution
          localStorage.setItem('authToken', token);
          setIsCodeValidated(true);
        } else {
          console.error("No token received despite successful response.");
          setIsCodeValidated(false);
        }
      } else {
        let message:any = "Validation failed.";
        const messageMapper = {
          'INVALIDFIRSTNAME': "Enter valid student first name",
          'INVALIDSECONDNAME': "Enter valid student last name",
          'INVALIDCLAIMCODE': "Enter valid student code",
          'INVALIDREGSTATUS': "Invalid Registration Status",
          'UTILIZEDCLAIMCD': "Student code already utilized. Please enter another valid student code",
          'INVALIDCONTRCTID': {
            message: "You are not allowed to submit your scores from this portal. Please use ",
            link: "https://my.act.org",
            linkText: "MyACT",
          },
          'CONTRACTIDNOTCONFIGURED': "This functionality is not currently available to submit your scores to colleges, please contact your school admin",
        }
        if (authenticateStudentResponse?.code) {
          const mappedMessage = messageMapper[authenticateStudentResponse?.code as keyof typeof messageMapper];
          if (typeof mappedMessage === 'string') {
            message = mappedMessage;
          } else if (mappedMessage && typeof mappedMessage === 'object') {
            message = {
              message: mappedMessage.message,
              href: mappedMessage.link,
              linkText: mappedMessage.linkText,
            }
          }
        }

        showNotification(message);
        setIsCodeValidated(false);
      }
    } catch (error) {
      console.error("Validation Error:", error);
      showNotification("An error occurred during validation.");
      setIsCodeValidated(false);
    }
  };

  const startSubmissionWithValidation = async () => {
    if (process.env.REACT_APP_ENABLE_CONTACT_INFO === 'true') {
      const input = {
        street1: contactInfo.streetAddress1,
        street2: contactInfo.streetAddress2 || '',
        city: contactInfo.city,
        state: contactInfo.state,
        countryCode: contactInfo.country === 'United States' ? 'US' : contactInfo.country,
        postalCode: contactInfo.zipCode,
      };
      try {
        const { data } = await validateAddr({ variables: { input } });
        const result = data?.validateAddress;
        if (!result) {
          setIsConfirmSubmitModalOpen(true);
          return;
        }
        setAddrResult(result);
        setSelectedAddress(result.analysis === 'MATCH' ? 'suggested' : 'entered');
        setShowAddrModal(true);
      } catch (e) {
        showNotification('Unable to validate address at the moment.');
        setIsConfirmSubmitModalOpen(true);
      }
    } else {
      setIsConfirmSubmitModalOpen(true);
    }
  };

  const handleAcceptValidatedAddress = () => {
    if (selectedAddress === 'suggested' && addrResult?.suggestedData?.profile_format) {
      const s = addrResult.suggestedData.profile_format;
      setContactInfo({
        streetAddress1: s.street1 || '',
        streetAddress2: s.street2 || '',
        city: s.city || '',
        state: s.state || '',
        stateCode: s.state_code || '',
        zipCode: s.postal_code || '',
        country: s.country_code === 'US' ? 'United States' : (s.country_code || ''),
      });
    }
    setShowAddrModal(false);
    setIsConfirmSubmitModalOpen(true);
  };

  // Resets all form fields and state to their initial values
  const handleCancel = () => {
    setIsConfirmCancelModalOpen(true);
  };

  const confirmCancel = () => {
    setIsCodeValidated(false);
    setUserDetails({ firstName: "", lastName: "", studentCode: "" });
    setSelectedColleges([]);
    setContactInfo({
      streetAddress1: "",
      streetAddress2: "",
      city: "",
      state: "",
      stateCode: "",
      zipCode: "",
      country: "United States",
    });
    setIsModalOpen(false);
    setNotificationMessage("");
    setIsConfirmCancelModalOpen(false);
    setSubmitAttempted(false);
  };

  // Submits the selected colleges along with their order
  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (enableContactInfo && hasContactErrors) return;
    await startSubmissionWithValidation();
  };

  const handleRetry = (transactionId: any, confirmSubmit: any) => {
    setIsRetryPopupVisible(false); // Close the popup
    confirmSubmit(); // Call confirmSubmit again to retry the process
  };

  const handleCancelRetry = (transactionId: any) => {
    setTransactionInProgress(false);
    setIsRetryPopupVisible(false); // Close the popup
  };

  const confirmSubmit = async () => {
    setIsConfirmSubmitModalOpen(false)
    setTransactionInProgress(true);
    try {
      if (enableContactInfo) {
        try {
          const input = {
            streetAddress1: contactInfo.streetAddress1,
            streetAddress2: contactInfo.streetAddress2 || '',
            city: contactInfo.city,
            state: contactInfo.state,
            stateCode: contactInfo.stateCode,
            zipCode: contactInfo.zipCode,
            country: 'United States',
          };

          const { data } = await updateContactInfo({ variables: { input } });
          if (data?.updateContactInformation) {
            setContactInfo(data.updateContactInformation);
          }
        } catch (e) {
          console.error('Update contact info failed', e);
        }
      }

      // Step 1: Submit colleges
      const response = await submitColleges({
        variables: {
          collegeInput: {
            recipients: selectedColleges.map((college, index) => ({
              rank: index + 1,
              code: college.id,
            })),
          },
        },
      });

      const initiatedTransactionId = response?.data?.saveCollegeChoice?.initiatedTransactionId;
      setInitiatedTransactionId(initiatedTransactionId);
      if (!initiatedTransactionId) {
        throw new Error("Initiated Transaction ID not received.");
      }
      await pollInitiatedTransactionStatus(initiatedTransactionId);
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message.includes("Unauthorized")) {
          showNotification("Session time out. Please validate the student code once again.");
          setIsSessionTimeOut(true);
        }
      } else {
        setTransactionInProgress(false);
      }
      setTransactionInProgress(false);
    } finally {
      setIsConfirmSubmitModalOpen(false);
    }
  };

  const pollTransactionStatus = async (transactionId: any, initiatedTransactionId: any) => {
    const pollingUrl = `${process.env.REACT_APP_EVENT_STREAM_CONFIRM_URL}/${transactionId}/watch`;
    pollEventStream(
      pollingUrl,
      (eventData) => {
        console.log("eventData", eventData);
        if (eventData?.status === "COMPLETED") {
          console.log("final Transaction Completed!");
          setTransactionInProgress(false);
          showNotification("Scores successfully shared with selected colleges.");
          setIsSubmissionSuccessful(true);
        }
        if (eventData?.status === "ERROR") {
          console.log("Transaction Failed!");
          setTransactionInProgress(false);
          handleTransactionError(transactionId, initiatedTransactionId);
        }
      },
      (error) => {
        console.error("Error while polling transaction status:", error);
        handleTransactionError(transactionId, initiatedTransactionId);
        setTransactionInProgress(false);
      }
    );
  };

  const pollInitiatedTransactionStatus = async (initiatedTransactionId: any) => {
    const pollingUrl = `${process.env.REACT_APP_EVENT_STREAM_ASR_URL}/${initiatedTransactionId}/watch`;
    pollEventStream(
      pollingUrl,
      async (eventData) => {
        console.log("Transaction Completed!");

        // Call confirm registration mutation
        const confirmRegistrationData = await confirmRegistration({
          variables: {
            confirmRegistrationInput: {
              initiatedTransactionId: initiatedTransactionId,
            },
          },
        });

        console.log("Confirm Registration Data:", confirmRegistrationData);
        const transactionId = confirmRegistrationData?.data?.confirmRegistration?.transactionId;
        setTransactionId(transactionId);
        if (transactionId) {
          await pollTransactionStatus(transactionId, initiatedTransactionId);
        }
      },
      (error) => {
        console.error("Error while polling transaction status:", error);
        setTransactionInProgress(false);
      }
    );
  };


  const handleTransactionError = async (transactionId: any, initiatedTransactionId: any) => {
    try {
      await productRemove({
        variables: {
          productRemoveInput: {
            transactionId,
            initiatedTransactionId,
          },
        },
      });

      setTransactionInProgress(false);
      setIsRetryPopupVisible(true);
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message.includes("Unauthorized")) {
          showNotification("Session time out. Please validate the student code once again.");
          setIsSessionTimeOut(true);
        }
      } else {
        setTransactionInProgress(false);
      }
      setTransactionInProgress(false);
      console.error("Error while removing added colleges:", err);
    }
  };

  // Displays a notification modal with the specified message and icon
  const showNotification = (message: string) => {
    setNotificationMessage(message);
    setIsModalOpen(true);
  };

  // Updates the `selectedColleges` state when the user modifies the selection
  const handleSelectionChange = (
    selected: {
      id: string;
      label: string;
      order: number;
      value: string;
      city: string;
      state: string;
    }[]
  ) => {
    setSelectedColleges(selected);
  };

  // Closes the notification modal
  const closeModal = () => {
    setIsModalOpen(false);
    setNotificationMessage("");
  };

  // Determines if the submit button should be disabled
  const isBaseInvalid =
    !userDetails.firstName ||
    !userDetails.lastName ||
    !userDetails.studentCode ||
    selectedColleges.length === 0;

  const isSubmitDisabled = isBaseInvalid || hasContactErrors;

  return (
    <Router>
      <div className="form-container">
        <Logo />
        <Routes>
          <Route path="/" element={<Navigate to="/student-details" />} />
          <Route path="/student-details" element={
            <>
              <InfoSection message="Enter your Student First Name and Student Last Name name exactly as they appear on your personalized Non-Test Instructions for Students form." />
              <UserDetailsForm
                userDetails={userDetails}
                setUserDetails={setUserDetails}
                handleVerifyCode={handleVerifyCode}
              />
            </>
          }
          />
          <Route path="/submit-colleges" element={
            <Box>
              <h2>Selected Student Details:</h2>
              <ReadOnlyUserDetailsForm userDetails={userDetails} />

              {enableContactInfo && submitAttempted && hasContactErrors}

              {enableContactInfo && <ContactInformation
                contactInfo={contactInfo}
                setContactInfo={setContactInfo}
                submitAttempted={submitAttempted}
              />}

              <CollegeDropdown
                onSelectionChange={handleSelectionChange}
                selectedColleges={selectedColleges}
                setSelectedColleges={setSelectedColleges}
                setIsSessionTimeOut={setIsSessionTimeOut}
                showNotification={showNotification}
              />
              <FormButtons
                isSubmitDisabled={isSubmitDisabled}
                handleSubmit={handleSubmit}
                handleCancel={handleCancel}
              />
            </Box>
          } />
        </Routes>
        {isCodeValidated ?
          <Navigate to="/submit-colleges" /> :
          <Navigate to="/student-details" />}


        <NotificationModal
          isOpen={isModalOpen}
          onClose={closeModal}
          message={notificationMessage}
          buttonText="OK"
          onConfirm={isSubmissionSuccessful || isSessionTimeOut ? () => { window.location.reload(); } : closeModal}
          disableBackdropClick={isSubmissionSuccessful || isSessionTimeOut}
        />

        <NotificationModal
          isOpen={isConfirmCancelModalOpen}
          onClose={() => setIsConfirmCancelModalOpen(false)}
          message="Do you want to cancel? Any changes made will be lost"
          buttonText="Yes"
          onConfirm={confirmCancel}
          secondaryButtonText="No"
          onSecondaryButtonClick={() => setIsConfirmCancelModalOpen(false)}
        />

        <NotificationModal
          isOpen={isConfirmSubmitModalOpen}
          onClose={() => setIsConfirmSubmitModalOpen(false)}
          message="Verify score recipient(s). Once submitted, recipient list cannot be changed"
          buttonText="Submit"
          onConfirm={confirmSubmit}
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={() => setIsConfirmSubmitModalOpen(false)}
        />

        <NotificationModal
          isOpen={isRetryPopupVisible}
          onClose={() => setIsRetryPopupVisible(false)} // Close the modal
          message="The transaction encountered an error. Would you like to retry or cancel the submission?"
          buttonText="Retry"
          onConfirm={() => {
            setIsRetryPopupVisible(false); // Close popup on retry
            handleRetry(transactionId, confirmSubmit); // Retry logic
          }}
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={() => {
            setIsRetryPopupVisible(false); // Close popup on cancel
            handleCancelRetry(transactionId); // Cancel logic
          }}
        />

        <AddressValidationModal
          open={showAddrModal}
          loading={addrLoading}
          enteredView={addrResult?.enteredView || []}
          suggestedView={addrResult?.suggestedView || []}
          analysis={addrResult?.analysis || 'NO_MATCH'}
          selected={selectedAddress}
          onSelect={setSelectedAddress}
          onAccept={handleAcceptValidatedAddress}
          onClose={() => setShowAddrModal(false)}
        />

        <Backdrop
          sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
          open={transactionInProgress}
          onClick={() => { setTransactionInProgress(false) }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </Router>

  );
}

export default MainApplicationForm;